import React from 'react';
import { useSelector } from 'react-redux';
import { Alert, AlertDescription } from '@client/components/ui/alert';
import { Skeleton } from '@client/components/ui/skeleton';
import { ContactSupportButton } from '@client/components/ContactSupportButton';
import { useAdminMode } from '@/hooks/useAdminMode';
import { AdminModeBanner } from '@/components/admin/AdminModeBanner';
import { OwnerInformationCard } from '@/components/admin/OwnerInformationCard';
import { selectSession } from '@/model/selectors';
import {
  useSubscriptionSeats,
  useSeatInvitationAcceptDecline,
  useSubscriptionViewManagement,
  SubscriptionOverviewCard,
  NonOwnerOverviewCard,
  SeatsTable,
  SeatDialogs,
  PendingSeatInvitationsSection,
  AcceptSeatInvitationDialog,
  PlanSelectorCard,
  type SubscriptionView,
} from '@client/components/advisor-account-seats';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@client/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@client/components/ui/alert-dialog';

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {/* Overview Card Skeleton */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48 rounded" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            <div className="min-w-[120px]">
              <Skeleton className="mb-2 h-4 w-20 rounded" />
              <Skeleton className="h-8 w-12 rounded" />
            </div>
            <div className="min-w-[120px]">
              <Skeleton className="mb-2 h-4 w-16 rounded" />
              <Skeleton className="h-8 w-24 rounded" />
            </div>
            <div className="min-w-[120px]">
              <Skeleton className="mb-2 h-4 w-28 rounded" />
              <Skeleton className="h-8 w-20 rounded" />
            </div>
            <div className="min-w-[120px]">
              <Skeleton className="mb-2 h-4 w-28 rounded" />
              <Skeleton className="h-8 w-40 rounded" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-10 w-28 rounded" />
            <Skeleton className="h-10 w-48 rounded" />
          </div>
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="space-y-4">
        {/* Search skeleton */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64 rounded" />
          <Skeleton className="h-4 w-32 rounded" />
        </div>
        {/* Table skeleton */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="space-y-4 p-4">
            <div className="flex gap-4">
              <Skeleton className="h-4 w-16 rounded" />
              <Skeleton className="h-4 w-24 rounded" />
              <Skeleton className="h-4 w-32 rounded" />
              <Skeleton className="h-4 w-28 rounded" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className="flex items-center gap-4"
              >
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-4 w-24 rounded" />
                <Skeleton className="h-4 w-48 rounded" />
                <Skeleton className="h-4 w-24 rounded" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorState({ error }: { error: string }) {
  return (
    <Alert variant="destructive">
      <AlertDescription>
        <div className="space-y-4">
          <p>{error || 'Failed to load subscription data'}</p>
          <ContactSupportButton
            subject="Subscription Data Load Error"
            className="w-full sm:w-auto"
          />
        </div>
      </AlertDescription>
    </Alert>
  );
}

function NoSubscriptionState() {
  return (
    <Alert>
      <AlertDescription>
        <div className="space-y-4">
          <p>No subscription found. If you believe this is an error, please contact support.</p>
          <ContactSupportButton
            subject="No Subscription Data Found"
            className="w-full sm:w-auto"
          />
        </div>
      </AlertDescription>
    </Alert>
  );
}

interface AdvisorAccountSeatsProps {
  showAdvisorSearch?: boolean;
  adminMode?: boolean;
}

export function AdvisorAccountSeats({
  showAdvisorSearch: propShowAdvisorSearch = false,
  adminMode: propAdminMode = false,
}: AdvisorAccountSeatsProps = {}) {
  // Admin mode state and handlers
  const { isWizardUser, selectedAdvisorId, adminModeActive, hasSelectedAdvisor } = useAdminMode({
    propAdminMode,
    propShowAdvisorSearch,
  });

  // Get session data for non-owner view
  const session = useSelector(selectSession);
  const seatStatus = session.stripe?.seatStatus;
  const advisorId = session.advisor?.advisorId;

  // In admin mode, use selectedAdvisorId; otherwise use null (defaults to session advisor)
  const targetAdvisorId = adminModeActive ? selectedAdvisorId : null;

  const {
    // Data
    seats,
    subscription,
    isOwner,
    creditBalance,
    unitPricePerSeat,
    activeSeatsCount,
    ownerInfo,
    currentAdvisorAssignedDateTime,
    currentAdvisorExpiresDateTime,

    // Fetch status
    isFetching,
    fetchError,

    // Mutation status
    isMutating,
    currentMutation,

    // Invitation preview
    invitationPreviewStatus,
    isPreviewingInvitation,
    invitationPreviewError,
    duplicateEmailError,
    invitationPreview,

    // Dialog state
    dialogType,
    emailInput,
    setEmailInput,
    showPreview,
    setShowPreview,
    expirationValue,
    setExpirationValue,

    // Actions
    openDialog,
    closeDialog,
    handlePreviewInvitation,
    handleSendInvitation,
    handleRemoveSeat,
    handleResendInvitation,
    handleDirectAssignSeat,
    refetch,
  } = useSubscriptionSeats(targetAdvisorId);

  // Seat invitation accept/decline hook
  const {
    pendingSeatInvitations,
    acceptDialogOpen,
    invitationToAccept,
    declineDialogOpen,
    invitationToDecline,
    isAccepting,
    isDeclining,
    isProcessing,
    handleAccept,
    confirmAccept,
    cancelAccept,
    handleDecline,
    confirmDecline,
    cancelDecline,
  } = useSeatInvitationAcceptDecline();

  // Check if invitations are still loading (null) vs loaded with no invitations ([])
  const isInvitationsLoading = pendingSeatInvitations === null;
  const hasPendingInvitations =
    pendingSeatInvitations !== null && pendingSeatInvitations.length > 0;

  // View management hook - use isInvitationsLoading OR isFetching
  const { view, setView } = useSubscriptionViewManagement({
    hasPendingInvitations,
    isFetching: isFetching || isInvitationsLoading,
  });

  // Show subscription management UI only when not in admin mode OR when advisor is selected
  const showSubscriptionUI =
    !adminModeActive || (adminModeActive && hasSelectedAdvisor && !isFetching && subscription);

  // Determine which subscription content to render
  // This simplifies the nested conditionals in the JSX
  type SubscriptionContentMode =
    | 'loading'
    | 'error'
    | 'no-subscription-admin'
    | 'no-subscription-user'
    | 'owner-view'
    | 'owner-inactive-view'
    | 'non-owner-needs-subscription'
    | 'non-owner-view'
    | 'none';

  const isSubscriptionInactive =
    subscription?.subscriptionStatus === 'canceled' ||
    subscription?.subscriptionStatus === 'unpaid';

  const getSubscriptionContentMode = (): SubscriptionContentMode => {
    if (isFetching) return 'loading';
    if (fetchError) return 'error';
    if (!subscription) {
      if (adminModeActive && hasSelectedAdvisor) return 'no-subscription-admin';
      if (!adminModeActive && advisorId) return 'no-subscription-user';
      return 'none';
    }
    if (!showSubscriptionUI) return 'none';
    if (isOwner) {
      if (isSubscriptionInactive) return 'owner-inactive-view';
      return 'owner-view';
    }
    // Non-owner needs subscription if: no seat status, or seat is not active
    if (advisorId && seatStatus !== 'active') return 'non-owner-needs-subscription';
    return 'non-owner-view';
  };

  const subscriptionContentMode = getSubscriptionContentMode();

  // Helper to render OwnerInformationCard for non-owner views in admin mode
  const showAdminOwnerInfo = adminModeActive && ownerInfo && subscription?.ownerAdvisorId;
  const adminOwnerInfoCard = showAdminOwnerInfo ? (
    <div className="mt-4">
      <OwnerInformationCard
        ownerInfo={{
          advisorId: subscription!.ownerAdvisorId!,
          advisorName: ownerInfo.advisorName,
          advisorEmail: ownerInfo.advisorEmail || null,
          organization: ownerInfo.organization,
        }}
      />
    </div>
  ) : null;

  return (
    <div className="flex-1">
      <AdminModeBanner hidden={propAdminMode} />

      <main className="h-screen grow px-4 pb-12 pt-3 sm:px-6 lg:px-8">
        <DashboardPageHeader title="Subscription" />

        <div className="flex flex-col">
          {/* View Toggle - only show when there are pending invitations */}
          {hasPendingInvitations && (
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">View</span>
                <Select
                  value={view}
                  onValueChange={(value: SubscriptionView) => setView(value)}
                >
                  <SelectTrigger className="w-[160px] text-gray-700">
                    <SelectValue placeholder="Select view" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subscription">Subscription</SelectItem>
                    <SelectItem value="invitations">
                      Invitations ({pendingSeatInvitations.length})
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* View Description */}
          <p className="mb-4 text-sm text-gray-700">
            {view === 'subscription' &&
              'Manage your subscription seats and invite new team members.'}
            {view === 'invitations' &&
              hasPendingInvitations &&
              'Review subscription invitations from other advisors who want you to join their subscription.'}
          </p>

          {/* Invitations View */}
          {view === 'invitations' && (
            <div>
              <PendingSeatInvitationsSection
                invitations={pendingSeatInvitations}
                onAccept={handleAccept}
                onDecline={handleDecline}
                isProcessing={isProcessing}
                decliningInvitationId={isDeclining ? invitationToDecline?.subscriptionSeatId : null}
              />
            </div>
          )}

          {/* Subscription View */}
          {view === 'subscription' && (
            <>
              {subscriptionContentMode === 'loading' && <LoadingSkeleton />}

              {subscriptionContentMode === 'error' && <ErrorState error={fetchError!} />}

              {subscriptionContentMode === 'no-subscription-admin' && <NoSubscriptionState />}

              {subscriptionContentMode === 'no-subscription-user' && (
                <PlanSelectorCard advisorId={advisorId!} />
              )}

              {subscriptionContentMode === 'owner-view' && (
                <div className="space-y-4">
                  <SubscriptionOverviewCard
                    subscription={subscription!}
                    activeSeatsCount={activeSeatsCount}
                    creditBalance={creditBalance}
                    unitPricePerSeat={unitPricePerSeat}
                    isOwner={isOwner}
                    onAddSeat={() => openDialog('addSeat')}
                  />
                  <SeatsTable
                    seats={seats}
                    subscription={subscription!}
                    isOwner={isOwner}
                    isMutating={isMutating}
                    isFetching={isFetching}
                    currentMutation={currentMutation}
                    onRemoveSeat={(seatId: string) => openDialog('removeSeat', seatId)}
                    onResendInvitation={handleResendInvitation}
                    onRefresh={refetch}
                  />
                </div>
              )}

              {subscriptionContentMode === 'owner-inactive-view' && (
                <div className="space-y-4">
                  <SubscriptionOverviewCard
                    subscription={subscription!}
                    activeSeatsCount={activeSeatsCount}
                    creditBalance={creditBalance}
                    unitPricePerSeat={unitPricePerSeat}
                    isOwner={isOwner}
                    onAddSeat={() => openDialog('addSeat')}
                  />
                  <PlanSelectorCard
                    advisorId={advisorId!}
                    variant="restart"
                  />
                </div>
              )}

              {subscriptionContentMode === 'non-owner-needs-subscription' && (
                <>
                  {adminOwnerInfoCard}
                  <NonOwnerOverviewCard
                    ownerInfo={ownerInfo}
                    currentAdvisorAssignedDateTime={currentAdvisorAssignedDateTime}
                    currentAdvisorExpiresDateTime={currentAdvisorExpiresDateTime}
                    seatStatus={seatStatus}
                  />
                  <PlanSelectorCard
                    advisorId={advisorId!}
                    className="mt-4"
                  />
                </>
              )}

              {subscriptionContentMode === 'non-owner-view' && (
                <>
                  {adminOwnerInfoCard}
                  <NonOwnerOverviewCard
                    ownerInfo={ownerInfo}
                    currentAdvisorAssignedDateTime={currentAdvisorAssignedDateTime}
                    currentAdvisorExpiresDateTime={currentAdvisorExpiresDateTime}
                    seatStatus={seatStatus}
                  />
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Seat Management Dialogs (only show for actual owners) */}
      {isOwner && (
        <SeatDialogs
          dialogType={dialogType}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          invitationPreviewStatus={invitationPreviewStatus}
          isPreviewingInvitation={isPreviewingInvitation}
          invitationPreviewError={invitationPreviewError}
          duplicateEmailError={duplicateEmailError}
          invitationPreview={invitationPreview}
          isMutating={isMutating}
          currentMutation={currentMutation}
          isWizardUser={isWizardUser}
          expirationValue={expirationValue}
          onExpirationChange={setExpirationValue}
          onClose={closeDialog}
          onPreviewInvitation={handlePreviewInvitation}
          onSendInvitation={handleSendInvitation}
          onRemoveSeat={handleRemoveSeat}
          onDirectAssignSeat={handleDirectAssignSeat}
        />
      )}

      {/* Accept Seat Invitation Dialog */}
      <AcceptSeatInvitationDialog
        open={acceptDialogOpen}
        invitation={invitationToAccept}
        onAccept={confirmAccept}
        onDecline={cancelAccept}
        isSubmitting={isAccepting}
      />

      {/* Decline Seat Invitation Confirmation Dialog */}
      <AlertDialog open={declineDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to decline the subscription invitation from ${
                invitationToDecline?.ownerFirstName && invitationToDecline?.ownerLastName
                  ? `${invitationToDecline.ownerFirstName} ${invitationToDecline.ownerLastName}`
                  : invitationToDecline?.ownerEmail
              }? The subscription owner will be notified.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelDecline}
              disabled={isDeclining}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-700 text-white hover:bg-red-700 disabled:opacity-50"
              onClick={confirmDecline}
              disabled={isDeclining}
            >
              {isDeclining ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Declining...
                </span>
              ) : (
                'Decline Invitation'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
